import { supabase } from './supabase/supabaseClient.js';

/**
 * Servicio de integración con YoolAdmin (FileMaker)
 * Maneja la sincronización bidireccional entre Supabase y FileMaker
 */
class YoolAdminIntegrationService {
  constructor() {
    this.syncInProgress = false;
    this.lastSyncTime = null;
    this.baseUrl = process.env.REACT_APP_YOOLADMIN_API_URL || 'http://localhost:3001/api/yooladmin';
  }

  /**
   * Verificar si hay una sincronización en progreso
   * @returns {boolean} Estado de sincronización
   */
  isSyncInProgress() {
    return this.syncInProgress;
  }

  /**
   * Obtener la última fecha de sincronización
   * @returns {Date|null} Fecha de última sincronización
   */
  getLastSyncTime() {
    return this.lastSyncTime;
  }

  /**
   * Sincronizar compañías desde YoolAdmin a Supabase
   * @returns {Promise<Object>} Resultado de la sincronización
   */
  async syncCompaniesFromYoolAdmin() {
    try {
      this.syncInProgress = true;
      
      // Registrar inicio de sincronización
      await this.logSyncOperation('companies', 'sync', 'pending', 'Iniciando sincronización de compañías');
      
      // 1. Obtener compañías de YoolAdmin
      const yoolAdminCompanies = await this.getYoolAdminCompanies();
      
      let syncedCount = 0;
      let errorCount = 0;
      const errors = [];
      
      // 2. Procesar cada compañía
      for (const company of yoolAdminCompanies) {
        try {
          await this.syncSingleCompany(company);
          syncedCount++;
        } catch (error) {
          errorCount++;
          errors.push({ company: company.nombre, error: error.message });
          console.error(`Error syncing company ${company.nombre}:`, error);
        }
      }
      
      this.lastSyncTime = new Date();
      
      // Registrar resultado de sincronización
      const status = errorCount === 0 ? 'success' : (syncedCount > 0 ? 'partial' : 'failed');
      await this.logSyncOperation('companies', 'sync', status, 
        `Sincronizadas: ${syncedCount}, Errores: ${errorCount}`, 
        { syncedCount, errorCount, errors }
      );
      
      return { 
        success: true, 
        synced: syncedCount, 
        errors: errorCount,
        details: errors
      };
    } catch (error) {
      console.error('Error syncing companies:', error);
      await this.logSyncOperation('companies', 'sync', 'failed', error.message);
      throw error;
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Obtener compañías desde YoolAdmin
   * @returns {Promise<Array>} Lista de compañías de YoolAdmin
   */
  async getYoolAdminCompanies() {
    try {
      const response = await fetch(`${this.baseUrl}/empresas`);
      if (!response.ok) {
        throw new Error(`Error fetching companies from YoolAdmin: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching YoolAdmin companies:', error);
      // Fallback: retornar datos mock para desarrollo
      return this.getMockCompanies();
    }
  }

  /**
   * Datos mock para desarrollo cuando YoolAdmin no está disponible
   * @returns {Array} Lista de compañías mock
   */
  getMockCompanies() {
    return [
      {
        uuid: 'mock-001',
        nombre: 'Empresa Demo 1',
        identificacion_fiscal: '12345678-9',
        direccion: 'Av. Principal 123',
        telefono: '+56912345678',
        email: 'contacto@empresa1.cl',
        sitio_web: 'https://empresa1.cl',
        logo_url: null
      },
      {
        uuid: 'mock-002',
        nombre: 'Empresa Demo 2',
        identificacion_fiscal: '87654321-0',
        direccion: 'Calle Secundaria 456',
        telefono: '+56987654321',
        email: 'info@empresa2.cl',
        sitio_web: 'https://empresa2.cl',
        logo_url: null
      }
    ];
  }

  /**
   * Sincronizar una compañía específica
   * @param {Object} yoolAdminCompany - Datos de la compañía desde YoolAdmin
   * @returns {Promise<string>} ID de la compañía en Supabase
   */
  async syncSingleCompany(yoolAdminCompany) {
    try {
      // Verificar si la compañía ya existe en Supabase
      const { data: existingCompany } = await supabase
        .from('companias')
        .select('id')
        .eq('rut', yoolAdminCompany.identificacion_fiscal)
        .single();

      const companyData = {
        nombre_corto: yoolAdminCompany.nombre,
        rut: yoolAdminCompany.identificacion_fiscal,
        razon_social: yoolAdminCompany.nombre,
        direccion: yoolAdminCompany.direccion,
        telefono: yoolAdminCompany.telefono,
        correo: yoolAdminCompany.email,
        sitio_web: yoolAdminCompany.sitio_web,
        logo_url: yoolAdminCompany.logo_url,
        sync_status: 'synced',
        last_sync_date: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      let supabaseCompanyId;

      if (existingCompany) {
        // Actualizar compañía existente
        const { data, error } = await supabase
          .from('companias')
          .update(companyData)
          .eq('id', existingCompany.id)
          .select('id')
          .single();

        if (error) throw error;
        supabaseCompanyId = data.id;
        
        await this.logSyncOperation('company', 'update', 'success', 
          `Compañía actualizada: ${yoolAdminCompany.nombre}`,
          { supabaseId: supabaseCompanyId, yoolAdminId: yoolAdminCompany.uuid }
        );
      } else {
        // Crear nueva compañía
        const { data, error } = await supabase
          .from('companias')
          .insert(companyData)
          .select('id')
          .single();

        if (error) throw error;
        supabaseCompanyId = data.id;
        
        await this.logSyncOperation('company', 'create', 'success', 
          `Compañía creada: ${yoolAdminCompany.nombre}`,
          { supabaseId: supabaseCompanyId, yoolAdminId: yoolAdminCompany.uuid }
        );
      }

      // Actualizar YoolAdmin con el ID de Supabase (si la API está disponible)
      try {
        await this.updateYoolAdminCompany(yoolAdminCompany.uuid, {
          supabase_company_id: supabaseCompanyId,
          last_sync_date: new Date().toISOString(),
          integration_status: 'synced'
        });
      } catch (error) {
        console.warn('Could not update YoolAdmin company:', error.message);
      }

      return supabaseCompanyId;
    } catch (error) {
      await this.logSyncOperation('company', 'sync', 'failed', 
        `Error sincronizando ${yoolAdminCompany.nombre}: ${error.message}`,
        { yoolAdminId: yoolAdminCompany.uuid }
      );
      throw error;
    }
  }

  /**
   * Actualizar compañía en YoolAdmin
   * @param {string} companyId - ID de la compañía en YoolAdmin
   * @param {Object} updateData - Datos a actualizar
   */
  async updateYoolAdminCompany(companyId, updateData) {
    try {
      const response = await fetch(`${this.baseUrl}/empresas/${companyId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error(`Error updating YoolAdmin company: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating YoolAdmin company:', error);
      throw error;
    }
  }

  /**
   * Sincronizar suscripciones de una compañía
   * @param {string} companyId - ID de la compañía
   * @returns {Promise<Object>} Resultado de la sincronización
   */
  async syncSubscriptions(companyId) {
    try {
      // 1. Obtener suscripciones de VirtualPos (mock para desarrollo)
      const virtualPosSubscriptions = await this.getVirtualPosSubscriptions(companyId);
      
      // 2. Obtener mapeo de YoolAdmin
      const yoolAdminMappings = await this.getYoolAdminSubscriptionMappings(companyId);

      let syncedCount = 0;
      
      // 3. Sincronizar cada suscripción
      for (const subscription of virtualPosSubscriptions) {
        await this.syncSingleSubscription(companyId, subscription, yoolAdminMappings);
        syncedCount++;
      }

      return { success: true, synced: syncedCount };
    } catch (error) {
      console.error('Error syncing subscriptions:', error);
      throw error;
    }
  }

  /**
   * Obtener suscripciones de VirtualPos (mock para desarrollo)
   * @param {string} companyId - ID de la compañía
   * @returns {Promise<Array>} Lista de suscripciones
   */
  async getVirtualPosSubscriptions(companyId) {
    // Mock data para desarrollo
    return [
      {
        id: `vpos_${companyId}_001`,
        planId: 'plan_basic',
        frequency: 'monthly',
        autoRenew: true,
        amount: 29990,
        currency: 'CLP',
        status: 'active',
        startDate: new Date().toISOString(),
        metadata: {
          features: ['basic_analytics', 'email_support'],
          limits: { users: 10, projects: 5 }
        }
      }
    ];
  }

  /**
   * Obtener mapeos de suscripciones desde YoolAdmin
   * @param {string} companyId - ID de la compañía
   * @returns {Promise<Array>} Lista de mapeos
   */
  async getYoolAdminSubscriptionMappings(companyId) {
    try {
      const response = await fetch(`${this.baseUrl}/subscription-mappings?empresa_id=${companyId}`);
      if (!response.ok) {
        return []; // Retornar array vacío si no hay mapeos
      }
      return await response.json();
    } catch (error) {
      console.warn('Could not fetch subscription mappings:', error.message);
      return [];
    }
  }

  /**
   * Sincronizar una suscripción específica
   * @param {string} companyId - ID de la compañía
   * @param {Object} subscription - Datos de la suscripción
   * @param {Array} mappings - Mapeos existentes
   */
  async syncSingleSubscription(companyId, subscription, mappings) {
    try {
      // Actualizar datos de suscripción en Supabase
      const { error } = await supabase
        .from('companias')
        .update({
          subscription_id: subscription.id,
          virtualpos_plan_id: subscription.planId,
          billing_frequency: subscription.frequency,
          auto_renew: subscription.autoRenew,
          sync_status: 'synced',
          last_sync_date: new Date().toISOString(),
          subscription_metadata: subscription.metadata
        })
        .eq('id', companyId);

      if (error) throw error;

      await this.logSyncOperation('subscription', 'sync', 'success', 
        `Suscripción sincronizada para compañía ${companyId}`,
        { subscriptionId: subscription.id, companyId }
      );
    } catch (error) {
      await this.logSyncOperation('subscription', 'sync', 'failed', 
        `Error sincronizando suscripción: ${error.message}`,
        { subscriptionId: subscription.id, companyId }
      );
      throw error;
    }
  }

  /**
   * Obtener métricas consolidadas para dashboard
   * @param {string} companyId - ID de la compañía
   * @returns {Promise<Object>} Datos del dashboard
   */
  async getCompanyDashboardData(companyId) {
    try {
      // Obtener datos de la vista consolidada
      const { data: metrics, error } = await supabase
        .from('company_dashboard_metrics')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (error) throw error;

      // Obtener datos adicionales de YoolAdmin si es necesario
      const yoolAdminData = await this.getYoolAdminCompanyData(companyId);

      return {
        ...metrics,
        yoolAdminData,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error getting dashboard data:', error);
      throw error;
    }
  }

  /**
   * Obtener datos específicos de YoolAdmin
   * @param {string} companyId - ID de la compañía en Supabase
   * @returns {Promise<Object|null>} Datos de YoolAdmin
   */
  async getYoolAdminCompanyData(companyId) {
    try {
      const response = await fetch(`${this.baseUrl}/empresas?supabase_company_id=${companyId}`);
      if (!response.ok) {
        return null;
      }

      const companies = await response.json();
      if (!companies || companies.length === 0) {
        return null;
      }

      const companyData = companies[0];
      
      // Obtener datos relacionados
      const [plans, subscriptionMappings] = await Promise.all([
        this.getYoolAdminPlans(companyData.uuid),
        this.getYoolAdminSubscriptionMappings(companyData.uuid)
      ]);

      return {
        company: companyData,
        plans,
        subscriptionMappings,
        totalPlans: plans.length,
        activePlans: plans.filter(p => p.status === 'active').length
      };
    } catch (error) {
      console.error('Error getting YoolAdmin data:', error);
      return null;
    }
  }

  /**
   * Obtener planes desde YoolAdmin
   * @param {string} empresaId - ID de la empresa en YoolAdmin
   * @returns {Promise<Array>} Lista de planes
   */
  async getYoolAdminPlans(empresaId) {
    try {
      const response = await fetch(`${this.baseUrl}/planes?empresa_id=${empresaId}`);
      if (!response.ok) {
        return [];
      }
      return await response.json();
    } catch (error) {
      console.warn('Could not fetch YoolAdmin plans:', error.message);
      return [];
    }
  }

  /**
   * Registrar operación de sincronización
   * @param {string} entityType - Tipo de entidad
   * @param {string} operation - Operación realizada
   * @param {string} status - Estado de la operación
   * @param {string} message - Mensaje descriptivo
   * @param {Object} data - Datos adicionales
   */
  async logSyncOperation(entityType, operation, status, message, data = null) {
    try {
      await supabase
        .from('yooladmin_sync_logs')
        .insert({
          sync_type: 'yooladmin_integration',
          entity_type: entityType,
          entity_id: data?.supabaseId || data?.companyId,
          supabase_id: data?.supabaseId,
          yooladmin_id: data?.yoolAdminId,
          operation,
          status,
          error_message: status === 'failed' ? message : null,
          sync_data: data,
          processing_time_ms: null // Se puede agregar medición de tiempo si es necesario
        });
    } catch (error) {
      console.error('Error logging sync operation:', error);
    }
  }

  /**
   * Obtener logs de sincronización
   * @param {Object} filters - Filtros para los logs
   * @returns {Promise<Array>} Lista de logs
   */
  async getSyncLogs(filters = {}) {
    try {
      let query = supabase
        .from('yooladmin_sync_logs')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters.entityType) {
        query = query.eq('entity_type', filters.entityType);
      }
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      return data;
    } catch (error) {
      console.error('Error getting sync logs:', error);
      throw error;
    }
  }
}

export const yoolAdminIntegration = new YoolAdminIntegrationService();
export { YoolAdminIntegrationService };
